import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { hasRole, isSignedIn, session, signOut } from '../lib/session';
import { tenant } from '../lib/tenant';
import { useT } from '../lib/i18n';
import { BrandLockup } from './Brand';
import { LanguageSwitch } from './LanguageSwitch';
import { ThemeSwitch } from './ThemeSwitch';
import { OfflineBanner } from './OfflineBanner';
import { ToastHost } from './Toast';
import { Icon } from './Icon';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { Modal } from './Modal';

interface NavLink {
  href: string;
  label: string;
}

// nav.* keys exist in packages/shared/src/i18n for the pilot-facing items; staff-only console
// links (Evaluator/Issue/Dashboard/Responder) stay English for now — they're operations tooling,
// not something a visiting pilot ever sees.
function navLinksFor(t: ReturnType<typeof useT>): NavLink[] {
  const links: NavLink[] = [{ href: '/', label: t('nav.home') }];
  if (hasRole('evaluator')) links.push({ href: '/evaluator', label: 'Evaluator' });
  if (hasRole('assistant', 'sdm', 'admin', 'dc')) links.push({ href: '/office', label: t('nav.office') });
  if (hasRole('responder')) links.push({ href: '/office/responder', label: 'Responder' });
  if (hasRole('sdm', 'admin')) links.push({ href: '/issue', label: 'Issue' });
  if (hasRole('sdm', 'dc', 'admin')) links.push({ href: '/dashboard', label: 'Dashboard' });
  links.push({ href: '/about', label: t('nav.about') });
  return links;
}

export interface AppShellProps {
  children: ComponentChildren;
}

/** The page frame: top bar (brand, role-based nav, language/theme, sign-in state), content, footer. Mount once in app.tsx. */
export function AppShell({ children }: AppShellProps) {
  const { path } = useLocation();
  const t = useT();
  const links = navLinksFor(t);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div class="app-shell">
      <a class="sr-only sr-only-focusable" href="#main">
        {t('nav.skipToContent')}
      </a>
      <header class="app-shell__topbar">
        <div class="app-shell__topbar-inner">
          <a href="/" aria-label={`${tenant.product.name} home`}>
            <BrandLockup />
          </a>
          <nav class="app-shell__nav app-shell__nav--desktop" aria-label={t('nav.primaryAria')}>
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                class="app-shell__nav-link"
                aria-current={path === link.href ? 'page' : undefined}
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div class="row u-gap-3 app-shell__topbar-tools">
            <LanguageSwitch />
            <ThemeSwitch />
            {isSignedIn.value ? (
              <Button variant="ghost" size="sm" iconLeft="logout" onClick={signOut}>
                {session.value?.displayName ?? t('office.header.signOut')}
              </Button>
            ) : (
              <a class="c-btn c-btn--secondary c-btn--sm" href="/login">
                <Icon name="lock" size={16} />
                <span>{t('nav.staffSignIn')}</span>
              </a>
            )}
          </div>
          <IconButton
            icon="menu"
            label={t('nav.menu')}
            class="app-shell__menu-btn"
            bordered
            onClick={() => setMenuOpen(true)}
          />
        </div>
        <OfflineBanner />
      </header>

      <Modal open={menuOpen} onClose={() => setMenuOpen(false)} title={t('nav.menu')}>
        <nav class="app-shell__mobile-nav" aria-label={t('nav.primaryAria')}>
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              class="app-shell__mobile-nav-link"
              aria-current={path === link.href ? 'page' : undefined}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div class="app-shell__mobile-nav-tools">
          <LanguageSwitch />
          <ThemeSwitch />
        </div>
        {isSignedIn.value ? (
          <Button variant="ghost" iconLeft="logout" block onClick={signOut} class="u-mt-4">
            {session.value?.displayName ?? t('office.header.signOut')}
          </Button>
        ) : (
          <a class="c-btn c-btn--secondary u-full-width u-mt-4" href="/login" onClick={() => setMenuOpen(false)}>
            <Icon name="lock" size={16} />
            <span>{t('nav.staffSignIn')}</span>
          </a>
        )}
      </Modal>

      <main class="app-shell__main" id="main">
        {children}
      </main>

      <footer class="app-shell__footer">
        <div class="app-shell__footer-inner">
          <div>
            <BrandLockup mono size="footer" />
            <p class="u-mt-2 u-mb-0">
              {tenant.product.name} · {tenant.product.cardName}
              <br />
              {tenant.office.name}
            </p>
          </div>
          <nav class="app-shell__footer-links" aria-label={t('nav.footerAria')}>
            <a href="/about">{t('nav.about')}</a>
            <a href="/verify">{t('nav.verify')}</a>
            <a href="/stats">{t('nav.stats')}</a>
            <a href="/about#privacy">{t('footer.privacyNotice')}</a>
          </nav>
        </div>
      </footer>
      <ToastHost />
    </div>
  );
}
